import React from "react";
import { GridCellKind } from "@glideapps/glide-data-grid";
import { CanvasRenderingContext2DExtended } from "../CanvasRenderingContext2DExtended.js";
import { rowHeight } from "../types.js";
import { GlideCell } from "./GlideCell.js";
class GlideToggleCell extends GlideCell {
  _cellKind = "toggle";
  trackWidth;
  trackHeight;
  thumbSize;
  spacingSize;
  fontSize;
  constructor(valueGetter) {
    super(valueGetter);
  }
  getRenderer(muiTheme) {
    const fontSizeProperty = muiTheme.typography.body1.fontSize;
    this.fontSize = typeof fontSizeProperty === "string" ? Number.parseInt(fontSizeProperty) : fontSizeProperty;
    const muiTrackSize = muiTheme.components.MuiSwitch.styleOverrides.root;
    this.trackWidth = muiTrackSize.width;
    this.trackHeight = muiTrackSize.height;
    const muiThumbSize = muiTheme.components.MuiSwitch.styleOverrides.thumb;
    this.thumbSize = muiThumbSize.width;
    this.spacingSize = parseInt(muiTheme.spacing(1));
    return {
      kind: GridCellKind.Custom,
      needsHover: true,
      needsHoverPosition: true,
      isMatch: (cell) => cell.data.kind === this._cellKind,
      draw: (args, cell) => {
        const { ctx, theme, rect, hoverX, hoverY, overrideCursor } = args;
        const { value, localizedValue, disabled, inProgress } = cell.data.value;
        const valueToRender = inProgress ? !value : value;
        this.drawToggle(ctx, muiTheme, theme, rect, valueToRender, disabled || inProgress);
        this.drawText(ctx, muiTheme, theme, rect, valueToRender, localizedValue);
        if (this.isPosInsideToggle(hoverX, hoverY, theme) && !disabled && !inProgress) {
          overrideCursor("pointer");
        }
      },
      onClick: (args) => {
        const { cell, posX, posY, theme } = args;
        const { onToggle, disabled, value, inProgress } = args.cell.data?.value ?? {};
        let result = void 0;
        if (this.isPosInsideToggle(posX, posY, theme) && onToggle && !disabled && !inProgress) {
          onToggle(!value);
          result = cell;
        }
        return result;
      }
    };
  }
  formatValue(value) {
    if (value?.value && typeof value?.value !== "boolean") {
      throw new Error(`Invalid value: ${value}`);
    }
    return value ?? null;
  }
  getCustomContent(cellValue) {
    const { value, localizedValue } = cellValue;
    return {
      kind: GridCellKind.Custom,
      copyData: value ? localizedValue.true : localizedValue.false,
      data: {
        kind: this._cellKind,
        value: cellValue,
        tooltip: ToggleCellTooltipContent
      },
      allowOverlay: false
    };
  }
  isCellEmpty({ value }, isSummary) {
    return isSummary || value !== true && value !== false;
  }
  drawToggle(ctx, muiTheme, theme, cellRect, value, disabled) {
    let trackColor = value ? muiTheme.palette.success.dark : muiTheme.palette.secondary.dark;
    if (disabled) {
      ctx.globalAlpha = 0.12;
      trackColor = muiTheme.palette.secondary.main;
    }
    const { x, y, height, width } = this.getToggleRectInsideCell(theme);
    const gridX = cellRect.x + x;
    const gridY = cellRect.y + y;
    ctx.beginPath();
    ctx.fillStyle = trackColor;
    ctx.roundRect(gridX, gridY, width, height, this.spacing(2));
    ctx.fill();
    ctx.globalAlpha = 1;
    const thumbMargin = 3;
    const thumbXPos = gridX + (value ? width - thumbMargin - this.thumbSize : thumbMargin);
    const thumbYPos = gridY + thumbMargin;
    ctx.fillStyle = muiTheme.palette.background.paper;
    ctx.beginPath();
    ctx.roundRect(thumbXPos, thumbYPos, this.thumbSize, this.thumbSize, this.thumbSize / 2);
    ctx.fill();
  }
  drawText(ctx, muiTheme, theme, cellRect, value, localizedValue) {
    const textFont = `${this.fontSize}px ${theme.fontFamily}`;
    ctx.font = textFont;
    ctx.fillStyle = muiTheme.palette.text.primary;
    const textRect = this.getTextRect(theme, cellRect, this.fontSize);
    const textCtx = new CanvasRenderingContext2DExtended(ctx);
    textCtx.fillTextWithEllipsis(value ? localizedValue.true : localizedValue.false, textRect.width, textRect.x, textRect.y);
  }
  getToggleRectInsideCell(theme) {
    return {
      x: theme.cellHorizontalPadding,
      y: rowHeight / 2 - this.trackHeight / 2,
      width: this.trackWidth,
      height: this.trackHeight
    };
  }
  isPosInsideToggle(x, y, theme) {
    const toggleRect = this.getToggleRectInsideCell(theme);
    return x >= toggleRect.x && x <= toggleRect.x + toggleRect.width && y >= toggleRect.y && y <= toggleRect.y + toggleRect.height;
  }
  getTextRect(theme, cellRect, fontSize) {
    const { x, width } = this.getToggleRectInsideCell(theme);
    const textX = cellRect.x + x + width + this.spacing(1.5);
    return {
      x: textX,
      y: cellRect.y + rowHeight / 2,
      width: cellRect.width - x - theme.cellHorizontalPadding,
      height: fontSize
    };
  }
  spacing(value) {
    if (!this.spacingSize) {
      throw Error("Spacing is not set or 0");
    }
    return this.spacingSize * value;
  }
}
function ToggleCellTooltipContent({ value: cellValue }) {
  const { value, localizedValue, inProgress } = cellValue;
  const valueToDisplay = inProgress ? !value : value;
  return /* @__PURE__ */ React.createElement("div", null, valueToDisplay ? localizedValue?.true : localizedValue?.false);
}
export {
  GlideToggleCell
};
//# sourceMappingURL=GlideToggleCell.js.map
